import {
  initialValues,
  type BulkTemplateFormType,
  type BulkTemplatePaymentRowType,
} from "./BulkTemplate.form";

import type { NetworkPayload } from "../../../composites";

export const API = {
  createBulkTemplate: (
    payload: API.CreateBulkTemplate.Request,
  ): NetworkPayload => {
    return {
      url: "bulk_payment_templates/",
      options: {
        method: "POST",
        payload,
      },
    };
  },

  getBulkTemplates: (
    offsetIndex: number = 0,
    pageSize: number = 100,
    queryString?: string,
  ): NetworkPayload => {
    const formattedQueryString = queryString ? `&${queryString}` : "";
    return {
      url: `bulk_payment_templates?offset=${offsetIndex}&limit=${pageSize}${formattedQueryString}`,
      options: {
        method: "GET",
      },
    };
  },

  getTemplate: (templateId: API.BulkTemplateId): NetworkPayload => {
    return {
      url: `bulk_payment_templates/${templateId}/`,
      options: {
        method: "GET",
      },
    };
  },

  getTemplateVersions: (groupId: API.BulkTemplateId): NetworkPayload => {
    return {
      url: `bulk_payment_templates/${groupId}/versions/`,
      options: {
        method: "GET",
      },
    };
  },

  getTemplateVersion: (
    groupId: API.BulkTemplateId,
    versionId: API.BulkTemplateVersionId,
  ): NetworkPayload => {
    return {
      url: `bulk_payment_templates/${groupId}/versions/${versionId}/`,
      options: {
        method: "GET",
      },
    };
  },

  getTemplateVersionById: (
    versionId: API.BulkTemplateVersionId,
  ): NetworkPayload => {
    return {
      url: `bulk_payment_template_versions/${versionId}/`,
      options: {
        method: "GET",
      },
    };
  },

  createTemplateVersion: (
    groupId: API.BulkTemplateId,
    payload: API.CreateTemplateVersion.Request,
  ): NetworkPayload => {
    return {
      url: `bulk_payment_templates/${groupId}/versions/`,
      options: {
        method: "POST",
        payload,
      },
    };
  },

  archiveTemplate: (
    templateId: API.BulkTemplateId,
    payload: API.ArchiveBulkTemplate.Request,
  ): NetworkPayload => {
    return {
      url: `bulk_payment_templates/${templateId}/archive`,
      options: {
        method: "PUT",
        payload,
      },
    };
  },

  createBulkPaymentFromTemplate: (
    idempotencyKey: UUID,
    payload: API.CreateBulkPaymentFromTemplate.Request,
  ): NetworkPayload => {
    return {
      url: "bulk_payments/templates/",
      options: {
        method: "POST",
        payload,
        idempotencyKey,
      },
    };
  },

  // returns bulk payments in following states:
  // scheduled, awaiting_approval, approved, in_progress, partially_completed
  getTemplateActivePayments: (
    templateId: API.BulkTemplateId,
  ): NetworkPayload => {
    return {
      url: `bulk_payments?template=${templateId}&state_not_in=completed,failed,expired,bulk_payment_canceled,rejected_approval,canceled,approval_expired&limit=100`,
      options: {
        method: "GET",
      },
    };
  },
};

/**
 * Transforms API response payment data to form payment row format.
 * Note: recipient is stored as UUID in the API response, but the form expects
 * the full recipient object. The caller must resolve recipient UUIDs to objects.
 */
export const transformPaymentDataToFormRow = (
  paymentData: API.BulkTemplatePaymentData,
  recipientMap: Record<string, API.Recipient>,
): BulkTemplatePaymentRowType => ({
  recipient: recipientMap[paymentData.recipient],
  description: paymentData.description,
  amount: paymentData.amount,
  transactionType:
    paymentData.transaction_type === "credit" ? "send" : "collect",
});

/**
 * Transforms API template version response to form values.
 * Note: payments require recipient resolution, so recipientMap must be provided.
 */
export const transformVersionToFormValues = (
  version: API.BulkTemplateVersion,
  recipientMap: Record<string, API.Recipient>,
): BulkTemplateFormType => ({
  ...initialValues,
  title: version.name,
  fromAccount: version.institution_account,
  secCode: version.sec_code ?? "",
  entryDesc: version.entry_desc ?? "",
  memo: version.note || "",
  description: version.description,
  groupPosting: version.group_posting,
  allowExpedite: version.max_expedite_choice > 0,
  payments: [...version.payment_data]
    .sort((a, b) => a.order - b.order)
    .map((payment) => transformPaymentDataToFormRow(payment, recipientMap)),
});
